// src/components/GridView/GridView.tsx
import {
  useRef,
  useMemo,
  useCallback,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { createPortal } from 'react-dom';
import ExcelJS from 'exceljs';
import { HotTable, HotColumn } from '@handsontable/react';
import type { HotTableClass } from '@handsontable/react';
import { registerAllModules } from 'handsontable/registry';
import 'handsontable/styles/handsontable.min.css';
import 'handsontable/styles/ht-theme-main.min.css';
import './GridView.css';

import { GridViewContext } from './GridViewContext';
import { GridTitleCell } from './renderers/GridTitleCell';
import { GridCellEditor } from './renderers/GridCellEditor';
import { buildColumnHeader } from './GridColumnHeader';
import { useGridData } from './hooks/useGridData';
import { useGridColumns, isCellEnabled, isCellSummatory } from './hooks/useGridColumns';
import { useHiddenColumns, getColumnApiKey } from './hooks/useHiddenColumns';
import { GridContextMenu } from './GridContextMenu';
import { ManageColumnsModal } from './ManageColumnsModal';
import { gridViewService } from './service';
import { socketService } from '@/services/socketService';
import {
  GRID_LICENSE_KEY,
  GRID_TITLE_COLUMN_WIDTH,
  GRID_DEFAULT_COLUMN_WIDTH,
  GRID_EDITABLE_FIELD_TYPES,
  GRID_HEADER_HEIGHT,
  FIELD_TYPES,
} from './types';
import type {
  GridViewProps,
  GridViewRef,
  GridRow,
  GridColumn,
  EditingCellState,
  GridCellRect,
  SaveMultipleFieldRequest,
} from './types';
import type { FieldOption } from './service';
import type { ConversationDetail } from '@/types/conversation';
import { authStorage } from '@/lib/storage';
import { sanitizeSheetName } from '@/lib/utils';
import { config as envConfig } from '@/config/env';
import { conversationBus } from '@/lib/conversationBus';
import type { ActionMenuConfig } from '@components/CalendarView/types';
import { GridViewSkeleton } from './GridViewSkeleton';
import DuplicateCard from '@/components/DuplicateCard/DuplicateCard';
import type { DuplicateCardRef } from '@/components/DuplicateCard/DuplicateCard';

// Register all Handsontable modules once
registerAllModules();

// ==================== Sort State ====================

type SortState = { key: string; dir: 'asc' | 'desc' } | null;

/** Type-aware comparator for a single pair of cell values.
 *  Null/empty values always sort to the end regardless of direction. */
function compareCell(
  av: string,
  bv: string,
  fieldTypeId: number | undefined,
  dir: 'asc' | 'desc',
): number
{
  const aEmpty = av === '';
  const bEmpty = bv === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;

  let cmp = 0;
  if (fieldTypeId === FIELD_TYPES.DATE)
  {
    const da = new Date(av).getTime();
    const db = new Date(bv).getTime();
    cmp = !isNaN(da) && !isNaN(db) ? da - db : av.localeCompare(bv);
  }
  else if (
    fieldTypeId === FIELD_TYPES.INTEGER ||
    fieldTypeId === FIELD_TYPES.DECIMAL ||
    fieldTypeId === FIELD_TYPES.INTEGER_ALT
  )
  {
    const na = parseFloat(av);
    const nb = parseFloat(bv);
    cmp = !isNaN(na) && !isNaN(nb) ? na - nb : av.localeCompare(bv);
  }
  else
  {
    cmp = av.localeCompare(bv, undefined, { sensitivity: 'base' });
  }
  return dir === 'asc' ? cmp : -cmp;
}

// ==================== Helpers ====================

function buildRow(conv: ConversationDetail, _columns: GridColumn[]): GridRow
{
  const row: GridRow = {
    conversationId: conv.ConversationId,
    title: conv.Title ?? '',
    icon: conv.Icon ?? '',
    isRedFlag: conv.IsRedFlag ?? false,
    isImportant: conv.IsImportant ?? null,
    createdAt: conv.CreatedAt ?? '',
    configValueId: conv.ConfigValueId ?? 0,
    'col-title': conv.Title ?? '',
    'col-created-at': conv.CreatedAt ? formatDate(conv.CreatedAt) : '',
  };

  // Populate dynamic custom field cells.
  // Key matches useGridColumns: layer-{LayerId}-{fieldName.toLowerCase()}.
  // When multiple CFs share the same layer+name (merged column), first non-empty value wins.
  if (conv.LayerCustomFields)
  {
    for (const layer of conv.LayerCustomFields)
    {
      if (!layer.CustomFields) continue;
      for (const cf of layer.CustomFields)
      {
        const fieldName = (cf.FieldName ?? '').trim();
        const key = `layer-${layer.LayerId}-${fieldName.toLowerCase()}`;
        if (row[key] == null || row[key] === '')
        {
          row[key] = cf.FieldValue ?? '';
        }
      }
    }
  }

  return row;
}

function formatDate(dateStr: string): string
{
  try
  {
    const d = new Date(dateStr);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${mm}/${dd}/${d.getFullYear()}`;
  }
  catch
  {
    return dateStr;
  }
}

// ==================== Empty State ====================

function GridViewEmpty()
{
  return (
    <div className="gridview-container">
      <div className="gridview-empty-state">
        <span className="gridview-empty-icon material-icons" aria-hidden="true">
          grid_view
        </span>
        <p className="gridview-empty-text">No items to display</p>
      </div>
    </div>
  );
}

// ==================== Action Menu Config ====================

function buildActionMenuConfig(): ActionMenuConfig
{
  const token = authStorage.getAccessToken() || '';
  const webUrl = import.meta.env.VITE_WEB_URL || 'http://localhost/Corgee.WebClient/';
  const apiUrl = import.meta.env.VITE_JS_apiURL || 'http://localhost/Corgee.Web/';

  let storageData: Record<string, any> = {};
  try
  {
    storageData = JSON.parse(sessionStorage.getItem('C2_data') || '{}');
  }
  catch
  {
    /* ignore */
  }
  let allLayers: any[] = [];
  try
  {
    allLayers = JSON.parse(sessionStorage.getItem('C2_AllLayers') || '[]');
  }
  catch
  {
    /* ignore */
  }
  let layersToHide: any[] = [];
  try
  {
    layersToHide = JSON.parse(sessionStorage.getItem('C2_LayersToHide') || '[]');
  }
  catch
  {
    /* ignore */
  }

  return {
    api: { url: envConfig.api.baseUrl, token },
    config: { weburl: webUrl, apiurl: apiUrl } as any,
    storageData: {
      data: storageData,
      allLayers,
      layersToHide,
      layerId: parseInt(localStorage.getItem('C2_layerid') || '0'),
      conv_maxAllowedUpload: 20480000,
      enableChat: localStorage.getItem('C2_enableChat') === 'true',
      personid: parseInt(localStorage.getItem('C2_personid') || '0'),
    },
    configMenus: {
      conversation: [
        'goto-project',
        'close-infowindow',
        'opendetails-newtab',
        'mark-redflag',
        'mark-important',
        'follow-conv',
        'create-itemtype',
        'convert-to',
        'duplicate-project',
        'copylink-to-clipboard',
        'move-to',
        'copy-to',
        'submit-time',
        'edit-history',
        'edit',
        'delete',
        'duplicate-item',
        'archive',
      ],
      reply: [],
    },
    realItemTypes: {
      Users: { Name: 'People', Icon: 'icons-menu-account-box' },
      Tasks: { Name: 'Tasks', Icon: 'icons-menu-epic' },
    },
    userData: {
      imageProfile: 'Images/user.png?1.0.21.8',
      personName: 'User',
    },
  };
}

// ==================== Main Component ====================

export const GridView = forwardRef<GridViewRef, GridViewProps>(function GridView(
  {
    dashboardId,
    configValueId,
    projectId,
    filters,
    searchText,
    layersToHide,
    isActive = true,
    dashboardName,
    excludeColumnTitles,
    me,
    onOpenInfoWindow,
    onUpdateConversation,
    extraFilterParams,
  },
  ref,
)
{
  const hotRef = useRef<HotTableClass>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [editingCell, setEditingCell] = useState<EditingCellState | null>(null);
  // Mirrors editingCell for handleBeforeBeginEditing below, which is registered once with HOT
  // and should read the live value without needing editingCell in its own dependency array.
  const editingCellRef = useRef<EditingCellState | null>(null);
  editingCellRef.current = editingCell;
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const [fillSaving, setFillSaving] = useState(false);
  const [showCopyMsg, setShowCopyMsg] = useState(false);
  const copyMsgTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track if we should show "No More Items" message
  const [shouldShowNoMoreItems, setShouldShowNoMoreItems] = useState(false);
  const hasLoadedMoreRef = useRef(false);
  const [noMoreItemsPosition, setNoMoreItemsPosition] = useState<number | null>(null);
  const [hasScrollbar, setHasScrollbar] = useState(false);
  // Cache: "customFieldId-configValueId-layerId" → options list
  const optionsCacheRef = useRef<Map<string, FieldOption[]>>(new Map());

  // Invalidate options cache when context changes
  useEffect(() =>
  {
    optionsCacheRef.current.clear();
  }, [dashboardId, configValueId, projectId]);

  // Measure container height via ResizeObserver so HotTable gets an explicit pixel height.
  // This is required when the parent uses flex layout (flex: 1 1 auto / min-height: 0)
  // because HotTable's height="100%" has no pixel reference in that case.
  useEffect(() =>
  {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries =>
    {
      const h = entries[0]?.contentRect.height ?? 0;
      if (h > 0) setContainerHeight(h);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const {
    conversations,
    loading,
    loadingMore,
    hasMore,
    totalItems: _totalItems,
    loadMore,
    refresh,
    updateField,
    updateConversationState,
    removeConversation,
  } = useGridData({
    dashboardId,
    configValueId,
    projectId,
    filters,
    searchText,
    layersToHide,
    isActive,
    me,
    extraFilterParams,
  });

  const { columns: rawColumns, columnMap } = useGridColumns(conversations);
  const columns = useMemo(
    () =>
      excludeColumnTitles?.length
        ? rawColumns.filter(col => !excludeColumnTitles.includes(col.title))
        : rawColumns,
    [rawColumns, excludeColumnTitles],
  );

  // Hidden columns — persisted in API, drives visibleColumns.
  // ConfigValueId is always 1 for hidden-column calls regardless of the current route
  // (Bookmarks, Dashboards, ProjectDetails all use the same endpoint keyed by dashboardId/projectId).
  const { hiddenKeys, hideColumn, showColumn, applyHiddenColumns } = useHiddenColumns({
    dashboardId,
    configValueId: 1,
    projectId,
  });

  // visibleColumns: fixed cols always included; dynamic cols filtered by hiddenKeys
  const visibleColumns = useMemo(() =>
  {
    if (hiddenKeys.size === 0) return columns;
    return columns.filter((col, idx) =>
    {
      if (idx < 2) return true; // title + created-at always visible
      return !hiddenKeys.has(getColumnApiKey(col));
    });
  }, [columns, hiddenKeys]);

  // Context menu + Manage Columns modal state
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    column: GridColumn | null;
  } | null>(null);
  const [manageColumnsOpen, setManageColumnsOpen] = useState(false);

  // Flat row data for HotTable
  const rows = useMemo(
    () => conversations.map(c => buildRow(c, columns)),
    [conversations, columns],
  );

  // ==================== Manual Sort ====================

  const [sortState, setSortState] = useState<SortState>(null);

  const { activeRows, activeConversations } = useMemo(() =>
  {
    if (!sortState) return { activeRows: rows, activeConversations: conversations };
    const { key, dir } = sortState;
    const col = columnMap.get(key);
    const indices = conversations.map((_, i) => i);
    indices.sort((ai, bi) =>
    {
      if (key === 'col-created-at')
      {
        const av = conversations[ai]?.CreatedAt ?? '';
        const bv = conversations[bi]?.CreatedAt ?? '';
        if (!av && !bv) return 0;
        if (!av) return 1;
        if (!bv) return -1;
        const da = new Date(av).getTime();
        const db = new Date(bv).getTime();
        const cmp = !isNaN(da) && !isNaN(db) ? da - db : av.localeCompare(bv);
        return dir === 'asc' ? cmp : -cmp;
      }
      const av = String(rows[ai]?.[key] ?? '');
      const bv = String(rows[bi]?.[key] ?? '');
      return compareCell(av, bv, col?.fieldTypeId, dir);
    });
    return {
      activeRows: indices.map(i => rows[i]) as GridRow[],
      activeConversations: indices.map(i => conversations[i]) as ConversationDetail[],
    };
  }, [rows, conversations, sortState, columnMap]);

  // ==================== Editing ====================

  // Suspend HOT's keyboard listening while the editor is open, so it doesn't swallow keys.
  const isEditorOpen = editingCell != null;
  useEffect(() =>
  {
    if (!isEditorOpen) return;
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;

    // HOT re-arms on its own mouse handling after this effect runs, so re-suppress each time.
    const suppress = () => hot.unlisten();
    hot.addHook('afterListen', suppress);
    hot.unlisten();

    return () =>
    {
      hot.removeHook('afterListen', suppress);
      if (!hot.isDestroyed) hot.listen();
    };
  }, [isEditorOpen]);

  // Enter saves and moves down one row. Explicit because the editor stops the key from
  // reaching HOT, so its own enterMoves never fires.
  const handleCommitWithEnter = useCallback(() =>
  {
    const hot = hotRef.current?.hotInstance;
    if (!hot || !editingCell) return;
    const nextRow = editingCell.visualRow + 1;
    if (nextRow >= hot.countRows()) return;
    hot.selectCell(nextRow, editingCell.visualCol);
  }, [editingCell]);

  // Fires when the cell editor's full-viewport backdrop is clicked to save-and-close. That
  // backdrop covers the entire grid, so a click meant to start editing the NEXT cell also
  // only ever reaches the backdrop, not the cell underneath — the previous editor closes, but
  // HOT's selection is left on the cell just saved. The very next keystroke (typed expecting
  // to land in the new cell) then reopens THAT cell instead and overwrites what was just
  // saved to it — the fast-typing bug where an earlier value gets replaced by the next one.
  // Move HOT's selection to whatever is actually under the click so typing continues there.
  const handleEditorOutsideClick = useCallback((clientX: number, clientY: number) =>
  {
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;

    // Deferred a frame: at the moment this fires the backdrop that received the click is
    // still mounted (setEditingCell(null) hasn't been committed/painted yet), so
    // elementFromPoint right now would just find the backdrop again, not the cell under it.
    requestAnimationFrame(() =>
    {
      const el = document.elementFromPoint(clientX, clientY);
      const td = el?.closest('td');
      if (!td) return;
      const coords = hot.getCoords(td);
      if (!coords || coords.row < 0 || coords.col < 0) return;
      hot.selectCell(coords.row, coords.col);
    });
  }, []);

  // Asks HOT for the current <td> each time, since it recycles them while virtualising.
  const getEditingCellRect = useCallback((): GridCellRect | null =>
  {
    const hot = hotRef.current?.hotInstance;
    if (!hot || !editingCell) return null;
    const td = hot.getCell(editingCell.visualRow, editingCell.visualCol) as HTMLElement | null;
    if (!td) return null;
    const r = td.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return null;
    return { top: r.top, left: r.left, width: r.width, height: r.height };
  }, [editingCell]);

  // displayValue is what the cell shows after save (for combo/multi-select: the option text).
  // saveValue is what goes to the API (for combo/multi-select: the option Id/Value).
  const handleCellSave = useCallback(
    async (saveValue: any, displayValue?: string) =>
    {
      if (!editingCell) return;
      const { column, conversation, customField } = editingCell;
      const saved = editingCell;

      // Optimistic update: set the new value in HOT before closing the editor so
      // the cell shows the correct value immediately when the overlay disappears,
      // avoiding a flash of the old value during the async API call.
      const hot = hotRef.current?.hotInstance;
      if (hot)
      {
        hot.setDataAtRowProp(
          saved.visualRow,
          saved.column.key,
          displayValue ?? saveValue,
          'save-field',
        );
      }

      setEditingCell(null);

      // Use the actual CF's CustomFieldId (customField is from the conversation, so its
      // CustomFieldId is the correct one even when a unified column merges multiple IDs).
      const actualCfId = customField?.CustomFieldId ?? column.customFieldId!;
      const ok = await gridViewService.saveFieldValue({
        ...(customField ?? {}),
        CustomFieldValueId: customField?.CustomFieldValueId ?? 0,
        CustomFieldId: actualCfId,
        CoreTaskId: conversation.CoreTaskId ?? conversation.ConversationId,
        ConversationId: null,
        Value: saveValue,
        SelectedValuesText: displayValue ?? '',
        LayerId: column.layerId,
        SocketConnectionId: '',
      });

      if (ok)
      {
        // Update in-memory state so re-opening the editor shows the saved value
        updateField(
          conversation.ConversationId,
          saved.column.layerId!,
          actualCfId,
          saveValue,
          displayValue ?? String(saveValue),
        );
        conversationBus.emit('field-updated', {
          conversationId: conversation.ConversationId,
          coreTaskId: conversation.CoreTaskId ?? conversation.CoreTaskPhaseId ?? undefined,
          fieldName: saved.column.title,
          value: saveValue,
          layerId: saved.column.layerId ?? 0,
        });
      }
    },
    [editingCell],
  );

  // Ref keeps activeRows always current without recreating handleRenameTitle on each sort
  const activeRowsRef = useRef(activeRows);
  activeRowsRef.current = activeRows;

  const handleRenameTitle = useCallback(
    async (conversationId: number, newTitle: string) =>
    {
      const ok = await gridViewService.updateConversationTitle({
        ConversationId: conversationId,
        Title: newTitle,
        SocketConnectionId: socketService.connectionId || '',
      });

      if (ok)
      {
        const hot = hotRef.current?.hotInstance;
        if (hot)
        {
          const physicalRow = activeRowsRef.current.findIndex(
            r => r?.conversationId === conversationId,
          );
          if (physicalRow >= 0)
          {
            const visualRow =
              typeof hot.toVisualRow === 'function'
                ? (hot.toVisualRow(physicalRow) ?? physicalRow)
                : physicalRow;
            hot.setDataAtRowProp(visualRow, 'col-title', newTitle, 'title-rename');
          }
        }
        conversationBus.emit('title-updated', { conversationId, title: newTitle });
      }
    },
    [], // stable — reads from ref
  );

  // ==================== Red Flag / Important / Delete ====================

  // Refs keep conversations always current without re-creating callbacks on every render
  const conversationsRef = useRef(conversations);
  conversationsRef.current = conversations;

  const handleRedFlagToggle = useCallback(
    async (conversationId: number, isRedFlag: boolean) =>
    {
      const conv = conversationsRef.current.find(c => c.ConversationId === conversationId);
      if (!conv) return;
      // Optimistic update
      updateConversationState(conversationId, { IsRedFlag: isRedFlag });
      const ok = await gridViewService.markRedFlag(conv, isRedFlag);
      if (!ok)
      {
        // Revert on failure
        updateConversationState(conversationId, { IsRedFlag: !isRedFlag });
        return;
      }
      conversationBus.emit('redflag-updated', { conversationId, isRedFlag });
    },
    [updateConversationState],
  );

  const handleImportantToggle = useCallback(
    async (conversationId: number, isImportant: boolean | null) =>
    {
      const conv = conversationsRef.current.find(c => c.ConversationId === conversationId);
      if (!conv) return;
      // Optimistic update
      updateConversationState(conversationId, { IsImportant: isImportant });
      const ok = await gridViewService.markImportant(conv, isImportant);
      if (!ok)
      {
        // Revert on failure
        updateConversationState(conversationId, { IsImportant: conv.IsImportant });
        return;
      }
      conversationBus.emit('important-updated', { conversationId, isImportant });
    },
    [updateConversationState],
  );

  const handleDeleteConversation = useCallback(
    (conversationId: number) =>
    {
      removeConversation(conversationId);
      conversationBus.emit('conversation-deleted', { conversationId });
    },
    [removeConversation],
  );

  const duplicateCardRef = useRef<DuplicateCardRef>(null);
  const duplicateCardInitRef = useRef<((conv: any) => void) | null>(null);

  const handleDuplicateConversation = useCallback((_e: any, conv: any) =>
  {
    duplicateCardInitRef.current?.(conv);
    duplicateCardRef.current?.openModal();
  }, []);

  // ==================== Bus Subscriptions ====================

  // Keep stable refs to handlers so effect deps don't change on every render
  const updateConversationStateRef = useRef(updateConversationState);
  updateConversationStateRef.current = updateConversationState;
  const removeConversationRef = useRef(removeConversation);
  removeConversationRef.current = removeConversation;
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const updateFieldRef = useRef(updateField);
  updateFieldRef.current = updateField;

  useEffect(() =>
  {
    const hot = () => hotRef.current?.hotInstance;

    const setDataAtRowProp = (conversationId: number, prop: string, value: any) =>
    {
      const h = hot();
      if (!h) return;
      const physRow = activeRowsRef.current.findIndex(r => r?.conversationId === conversationId);
      if (physRow < 0) return;
      const visRow =
        typeof h.toVisualRow === 'function' ? (h.toVisualRow(physRow) ?? physRow) : physRow;
      h.setDataAtRowProp(visRow, prop, value, 'bus-update');
    };

    const unsubs = [
      conversationBus.on('title-updated', ({ conversationId, title }) =>
      {
        updateConversationStateRef.current(conversationId, { Title: title });
        setDataAtRowProp(conversationId, 'col-title', title);
      }),
      conversationBus.on('redflag-updated', ({ conversationId, isRedFlag }) =>
      {
        updateConversationStateRef.current(conversationId, { IsRedFlag: isRedFlag });
      }),
      conversationBus.on('important-updated', ({ conversationId, isImportant }) =>
      {
        updateConversationStateRef.current(conversationId, { IsImportant: isImportant });
      }),
      conversationBus.on('conversation-deleted', ({ conversationId }) =>
      {
        removeConversationRef.current(conversationId);
      }),
      conversationBus.on('layer-updated', ({ conversationId, layerId, layerData }) =>
      {
        // Update only affected cells without full grid reload
        const customFields: any[] = layerData?.CustomFields ?? [];
        customFields.forEach((cf: any) =>
        {
          const cfId = cf.CustomFieldId;
          const value = cf.Value ?? cf.FieldValue;
          const display = cf.SelectedValuesText ?? String(value ?? '');
          updateFieldRef.current(conversationId, layerId, cfId, value, display);
        });
      }),
      conversationBus.on('layers-updated', ({ conversationId, layers }) =>
      {
        updateConversationStateRef.current(conversationId, { ConversationLayers: layers as any });
        // GridTitleCell reads ConversationLayers from GridViewContext, so the context
        // update above is sufficient — no HOT setDataAtRowProp needed.
      }),
    ];

    return () => unsubs.forEach(fn => fn());
  }, []); // empty deps — all accessed via stable refs

  // Built once per mount — token is stable for the session
  const actionMenuConfig = useMemo(() => buildActionMenuConfig(), []);

  // Context value shared with React renderers
  const contextValue = useMemo(
    () => ({
      conversations: activeConversations,
      onOpenInfoWindow,
      onUpdateConversation,
      onRenameTitle: handleRenameTitle,
      onRedFlagToggle: handleRedFlagToggle,
      onImportantToggle: handleImportantToggle,
      onDeleteConversation: handleDeleteConversation,
      onDuplicateConversation: handleDuplicateConversation,
      actionMenuConfig,
    }),
    [
      activeConversations,
      onOpenInfoWindow,
      onUpdateConversation,
      handleRenameTitle,
      handleRedFlagToggle,
      handleImportantToggle,
      handleDeleteConversation,
      handleDuplicateConversation,
      actionMenuConfig,
    ],
  );

  // Column headers — function returning HTML strings with layer color bars
  const colHeaders = useCallback(
    (col: number): string =>
    {
      const column = visibleColumns[col];
      if (!column) return '';
      return buildColumnHeader(column);
    },
    [visibleColumns],
  );

  // afterGetColHeader — injects the layer color bar and sort icon into the <th> DOM element.
  // No click listeners here — a single delegated listener on the container handles all header clicks.
  const handleAfterGetColHeader = useCallback(
    (col: number, TH: HTMLTableCellElement) =>
    {
      const column = visibleColumns[col];

      // Color bar — dynamic columns only
      if (!column?.layerColor)
      {
        TH.querySelector('.gridview-col-header-color-bar')?.remove();
      }
      else
      {
        let bar = TH.querySelector<HTMLElement>('.gridview-col-header-color-bar');
        if (!bar)
        {
          bar = document.createElement('div');
          bar.className = 'gridview-col-header-color-bar';
          TH.insertBefore(bar, TH.firstChild);
        }
        bar.style.backgroundColor = column.layerColor;
      }

      // Sort icon — skip row-number header
      if (!column) return;

      // Keep column key up-to-date on the TH so the delegated listener can read it
      TH.dataset.sortColKey = column.key;

      // Inject icon inside .colHeader (inline next to title text).
      // Guard by element existence — if HOT clears colHeader content, re-inject.
      if (!TH.querySelector('.gridview-sort-icon'))
      {
        const icon = document.createElement('span');
        icon.className = 'gridview-sort-icon';
        const colHeader = TH.querySelector('.colHeader');
        (colHeader ?? TH).appendChild(icon);
      }
    },
    [visibleColumns],
  );

  // Single delegated click listener — attached once at mount, never re-added.
  // Reads TH.dataset.sortColKey set by afterGetColHeader.
  // setSortState is a stable React setter, safe to capture in an empty-deps effect.
  useEffect(() =>
  {
    const container = containerRef.current;
    if (!container) return;
    const handleHeaderClick = (e: MouseEvent) =>
    {
      const th = (e.target as Element).closest('thead th') as HTMLTableCellElement | null;
      if (!th) return;
      const colKey = th.dataset.sortColKey;
      if (!colKey) return;
      setSortState(prev =>
      {
        if (!prev || prev.key !== colKey) return { key: colKey, dir: 'asc' };
        if (prev.dir === 'asc') return { key: colKey, dir: 'desc' };
        return null;
      });
    };
    container.addEventListener('click', handleHeaderClick);
    return () => container.removeEventListener('click', handleHeaderClick);
  }, []); // empty deps — setSortState is stable

  // Update sort icon data-dir attributes whenever sortState changes (avoids HOT full re-render)
  useEffect(() =>
  {
    const container = containerRef.current;
    if (!container) return;
    container.querySelectorAll<HTMLElement>('.gridview-sort-icon').forEach(icon =>
    {
      const th = icon.closest('th') as HTMLTableCellElement | null;
      const colKey = th?.dataset.sortColKey;
      if (!colKey) return;
      if (sortState?.key === colKey)
      {
        icon.dataset.dir = sortState.dir;
      }
      else
      {
        delete icon.dataset.dir;
      }
    });
  }, [sortState]);

  // Cell properties — marks non-applicable cells as read-only + grey
  const cells = useCallback(
    (row: number, col: number) =>
    {
      // Title column: custom renderer, rename handled via React dblclick
      if (col === 0) return { readOnly: true };
      // Created-at column: display-only
      if (col === 1) return { readOnly: true };

      const column = visibleColumns[col];
      if (!column) return {};

      const hotInstance = hotRef.current?.hotInstance;
      const physicalRow =
        hotInstance && typeof hotInstance.toPhysicalRow === 'function'
          ? (hotInstance.toPhysicalRow(row) ?? row)
          : row;

      const conversation = activeConversations[physicalRow];

      if (!conversation || !isCellEnabled(conversation, column))
      {
        return { readOnly: true, className: 'gridview-cell-disabled' };
      }

      // Summatory fields aggregate values across phases — always read-only
      if (isCellSummatory(conversation, column))
      {
        return { readOnly: true, className: 'gridview-cell-disabled' };
      }

      // Non-editable field types (multi-select, combo, image)
      if (column.fieldTypeId != null && !GRID_EDITABLE_FIELD_TYPES.has(column.fieldTypeId))
      {
        return { readOnly: true, className: 'gridview-cell-disabled' };
      }

      // Cell is editable — do NOT mark as readOnly so beforeBeginEditing fires.
      // beforeBeginEditing returns false to cancel HOT's native editor and open our custom one.
      return {};
    },
    [visibleColumns, activeConversations],
  );

  // beforeBeginEditing — intercept all native HOT editing, open our custom editor for valid cells
  const handleBeforeBeginEditing = useCallback(
    (visualRow: number, visualCol: number): boolean =>
    {
      // Col 0 (title): rename handled by React dblclick in GridTitleCell
      if (visualCol === 0) return false;
      // Col 1 (created-at): read-only
      if (visualCol === 1) return false;

      // Our custom editor is already open for this exact cell — a rapid extra click/dblclick
      // landing on the still-open editor's cell (HOT doesn't know it's "in edit mode" here,
      // since we always return false below to cancel HOT's own native editor) would otherwise
      // re-run this whole function and call setEditingCell again with the value re-read from
      // activeConversations — still the stale, unsaved server value — wiping out whatever the
      // user had already typed into the open editor. Leave the in-progress session alone.
      const alreadyEditingThisCell =
        editingCellRef.current?.visualRow === visualRow &&
        editingCellRef.current?.visualCol === visualCol;
      if (alreadyEditingThisCell) return false;

      const column = visibleColumns[visualCol];
      if (!column) return false;

      // Non-editable field types
      if (column.fieldTypeId == null || !GRID_EDITABLE_FIELD_TYPES.has(column.fieldTypeId))
      {
        return false;
      }

      const hot = hotRef.current?.hotInstance;
      if (!hot) return false;

      const physicalRow =
        typeof hot.toPhysicalRow === 'function'
          ? (hot.toPhysicalRow(visualRow) ?? visualRow)
          : visualRow;

      const conversation = activeConversations[physicalRow];
      if (!conversation || !isCellEnabled(conversation, column)) return false;
      if (isCellSummatory(conversation, column)) return false;

      // Get cell rect for positioning the editor
      const td = hot.getCell(visualRow, visualCol) as HTMLTableCellElement | null;
      const cellRect = td?.getBoundingClientRect() ?? { top: 0, left: 0, width: 200, height: 30 };

      // Find the custom field data — try all merged IDs so unified columns work correctly
      const layer = conversation.LayerCustomFields?.find(l => l.LayerId === column.layerId);
      const ids =
        column.customFieldIds ?? (column.customFieldId != null ? [column.customFieldId] : []);
      const cf = layer?.CustomFields?.find(f => ids.includes(f.CustomFieldId));

      setEditingCell({
        visualRow,
        visualCol,
        column,
        conversation,
        customField: cf,
        currentValue: cf?.FieldValue ?? '',
        cellRect: {
          top: cellRect.top,
          left: cellRect.left,
          width: cellRect.width,
          height: cellRect.height,
        },
      });

      // Return false to cancel native HOT editor
      return false;
    },
    [visibleColumns, activeConversations],
  );

  // Stable refs so scroll handler never becomes stale
  const hasMoreRef = useRef(hasMore);
  hasMoreRef.current = hasMore;
  const loadingMoreRef = useRef(loadingMore);
  loadingMoreRef.current = loadingMore;
  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;

  // ── Infinite scroll ──────────────────────────────────────────────────────
  // Strategy: listen on window in capture phase.
  // • scroll does NOT bubble, but the capture phase fires top-down on all ancestors.
  // • window capture catches every scroll event on the page regardless of HOT
  //   initialisation timing — no querySelector / RAF timing issues.
  // • We filter to our specific container so other scrollable panels are ignored.
  useEffect(() =>
  {
    const checkScroll = (target: HTMLElement) =>
    {
      if (!hasMoreRef.current || loadingMoreRef.current) return;
      const { scrollTop, scrollHeight, clientHeight } = target;
      // Only trigger when 300 px from the bottom and the element is actually scrollable
      if (scrollHeight > clientHeight && scrollHeight - scrollTop - clientHeight < 300)
      {
        loadMoreRef.current();
        hasLoadedMoreRef.current = true;
      }
    };

    const onScroll = (e: Event) =>
    {
      const target = e.target as HTMLElement;
      // Must be a wtHolder element inside our GridView container
      if (!target?.classList?.contains('wtHolder')) return;
      if (!containerRef.current?.contains(target)) return;
      checkScroll(target);
    };

    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    return () => window.removeEventListener('scroll', onScroll, { capture: true });
  }, []); // mount-only — reads everything via stable refs

  // afterScrollVertically — HOT hook fires after each HOT viewport scroll update
  const handleAfterScrollVertically = useCallback(() =>
  {
    if (!hasMoreRef.current || loadingMoreRef.current) return;
    const holder = containerRef.current?.querySelector<HTMLElement>('.ht_master .wtHolder');
    if (!holder) return;
    const { scrollTop, scrollHeight, clientHeight } = holder;
    if (scrollHeight > clientHeight && scrollHeight - scrollTop - clientHeight < 300)
    {
      loadMoreRef.current();
      hasLoadedMoreRef.current = true;
    }
  }, []);

  // Check if we should show "No More Items" message
  // Show it whenever there are no more items to load (!hasMore)
  useEffect(() =>
  {
    if (!hasMore && conversations.length > 0)
    {
      const holder = containerRef.current?.querySelector<HTMLElement>('.ht_master .wtHolder');
      const hot = hotRef.current?.hotInstance;
      if (!holder || !hot) return;

      const { scrollHeight, clientHeight } = holder;
      // Always show the message when there are no more items
      setShouldShowNoMoreItems(true);

      // Detect if there's a scrollbar
      const hasScroll = scrollHeight > clientHeight;
      setHasScrollbar(hasScroll);

      // Calculate position for non-scrollable content (show right after last row)
      if (!hasScroll)
      {
        const rowCount = hot.countRows();
        const rowHeight = 30.5;
        const headerHeight = GRID_HEADER_HEIGHT;
        const totalHeight = headerHeight + rowCount * rowHeight;
        setNoMoreItemsPosition(totalHeight);
      }
      else
      {
        setNoMoreItemsPosition(null);
      }
    }
    else
    {
      setShouldShowNoMoreItems(false);
      setNoMoreItemsPosition(null);
      setHasScrollbar(false);
    }
  }, [hasMore, conversations.length, loadingMore]);

  // beforeChange — filter out null values that HOT would autofill into cells
  const handleBeforeChange = useCallback(
    (changes: [number, string | number, any, any][], source: string) =>
    {
      if (source === 'Autofill.fill')
      {
        for (let i = 0; i < changes.length; i++)
        {
          const change = changes[i];
          if (change && change[3] == null) (changes as any)[i] = null;
        }
      }
    },
    [],
  );

  // handleFillRange — batch-processes fill-handle drag and copy-paste changes.
  // For simple types (text, numeric, date, link) the display value is saved as-is.
  // For combo / multi-select (types 7, 8, 9) the display text is resolved to IDs
  // by fetching the options list; cells whose text doesn't match any option are skipped.
  const handleFillRange = useCallback(
    async (changes: [number, string | number, any, any][]) =>
    {
      const hot = hotRef.current?.hotInstance;
      if (!hot) return;

      setFillSaving(true);

      // Helper: fetch + cache options for a column using the ServiceUrl from the field definition.
      // Type-7 (person multi-select): append configValueId/layerId/projectId context params.
      // Types 8/9 (combo): ServiceUrl already has its own query params — use as-is.
      // Falls back to GetListComboBox only if no ServiceUrl is available.
      const getOptions = async (
        column: GridColumn,
        conversationConfigValueId?: number,
      ): Promise<FieldOption[]> =>
      {
        const cfgId =
          column.fieldTypeId === FIELD_TYPES.MULTI_SELECT
            ? (conversationConfigValueId ?? configValueId)
            : configValueId;
        const cacheKey = `${column.customFieldId}-${cfgId ?? 0}-${column.layerId ?? 0}`;
        if (!optionsCacheRef.current.has(cacheKey))
        {
          let opts: FieldOption[];
          if (column.serviceUrl)
          {
            // Type 7: append context params so the API can filter by layer/project/configValue
            // Types 8/9: URL already contains its params (e.g. ?BasicFieldId=18) — pass empty params
            const isPersonMulti = column.fieldTypeId === FIELD_TYPES.MULTI_SELECT;
            opts = await gridViewService.getFieldOptionsByServiceUrl(
              column.serviceUrl,
              isPersonMulti
                ? {
                  configValueId: cfgId,
                  layerId: column.layerId,
                  projectId: projectId ? parseInt(projectId, 10) : undefined,
                }
                : {},
              column.textField,
              column.valueField,
            );
          }
          else
          {
            opts = await gridViewService.getFieldOptions(column.customFieldId!, {
              configValueId: cfgId,
              layerId: column.layerId,
              projectId: projectId ? parseInt(projectId, 10) : undefined,
            });
          }
          optionsCacheRef.current.set(cacheKey, opts);
        }
        return optionsCacheRef.current.get(cacheKey)!;
      };

      const saveRequests: SaveMultipleFieldRequest[] = [];
      const saveTypes: (1 | 2 | string)[] = [];
      const displayUpdates: Array<{ visualRow: number; prop: string; display: string }> = [];
      const fieldUpdates: Array<{
        conversationId: number;
        coreTaskId?: number;
        layerId: number;
        customFieldId: number;
        value: any;
        display: string;
      }> = [];

      for (const [visualRow, prop, _oldVal, newVal] of changes)
      {
        if (newVal == null) continue;

        const key = String(prop);
        if (key === 'col-title' || key === 'col-created-at') continue;

        const column = columnMap.get(key);
        if (!column?.customFieldId || column.fieldTypeId == null) continue;
        if (!GRID_EDITABLE_FIELD_TYPES.has(column.fieldTypeId)) continue;

        const physicalRow =
          typeof hot.toPhysicalRow === 'function'
            ? (hot.toPhysicalRow(visualRow) ?? visualRow)
            : visualRow;

        const conversation = activeConversations[physicalRow];
        if (!conversation || !isCellEnabled(conversation, column)) continue;
        if (isCellSummatory(conversation, column)) continue;

        const layer = conversation.LayerCustomFields?.find(l => l.LayerId === column.layerId);
        if (!layer) continue;

        // Resolve the set of CFs this conversation has for the unified column
        const cfIds =
          column.customFieldIds ?? (column.customFieldId != null ? [column.customFieldId] : []);
        const matchingCfs = layer.CustomFields?.filter(f => cfIds.includes(f.CustomFieldId)) ?? [];
        if (matchingCfs.length === 0) continue;

        let saveValue: any = String(newVal);
        let displayValue: string = String(newVal);

        // Resolve display text → IDs for combo / multi-select
        const isMulti = column.fieldTypeId === FIELD_TYPES.MULTI_SELECT;
        const isCombo =
          column.fieldTypeId === FIELD_TYPES.COMBO || column.fieldTypeId === FIELD_TYPES.COMBO_ALT;
        const isInteger = column.fieldTypeId === FIELD_TYPES.INTEGER;
        const isNumeric =
          isInteger ||
          column.fieldTypeId === FIELD_TYPES.DECIMAL ||
          column.fieldTypeId === FIELD_TYPES.INTEGER_ALT;

        if (isMulti || isCombo)
        {
          const options = await getOptions(column, (conversation as any).ConfigValueId);

          if (isMulti)
          {
            const names = String(newVal)
              .split(',')
              .map(s => s.trim())
              .filter(Boolean);
            const matched = names
              .map(name =>
                options.find(
                  o =>
                    String(o.Text ?? '')
                      .trim()
                      .toLowerCase() === name.toLowerCase(),
                ),
              )
              .filter((o): o is FieldOption => o != null);
            if (matched.length === 0) continue; // no match → skip cell
            saveValue = matched.map(o => o.Id).join(',');
            displayValue = matched.map(o => o.Text).join(', ');
          }
          else
          {
            const name = String(newVal).trim();
            const match = options.find(
              o =>
                String(o.Text ?? '')
                  .trim()
                  .toLowerCase() === name.toLowerCase(),
            );
            if (!match) continue; // no match → skip cell
            saveValue = String(match.Id);
            displayValue = String(match.Text).trim();
          }
        }
        else if (isNumeric)
        {
          // Validate numeric input: INTEGER must be a whole number, DECIMAL/INTEGER_ALT accepts decimals
          const strVal = String(newVal).trim().replace(',', '.');
          const num = isInteger ? parseInt(strVal, 10) : parseFloat(strVal);
          if (isNaN(num)) continue; // skip non-numeric values
          if (isInteger && !Number.isInteger(parseFloat(strVal))) continue; // skip decimals for integer fields
          saveValue = num;
          displayValue = String(num);
        }

        // Save to every matching CF in this conversation (handles merged-column duplicates)
        for (const cf of matchingCfs)
        {
          if (column.serviceUrl)
          {
            // Types 7/8/9 with ServiceUrl: send full CF payload, type "savePost"
            saveRequests.push({
              ...cf,
              CustomFieldValueId: cf.CustomFieldValueId ?? 0,
              CustomFieldId: cf.CustomFieldId,
              CoreTaskId: conversation.CoreTaskId ?? conversation.ConversationId,
              ConversationId: conversation.ConversationId,
              Value: saveValue,
              PreviousValue: null,
              SelectedValuesText: displayValue,
              LayerId: column.layerId,
              SocketConnectionId: '',
            });
            saveTypes.push('savePost');
          }
          else if (isNumeric || column.fieldTypeId === FIELD_TYPES.DATE)
          {
            // Types 2/3/4 numeric + Type 6 date: explicit payload matching backend expectation.
            // "save"     → direct _repo.Get(CustomFieldValueId) — requires CustomFieldValueId > 0
            // "savePost" → CoreTaskID DB lookup first (insert or update) — required when id = 0
            const cfValueId = cf.CustomFieldValueId ?? 0;
            saveRequests.push({
              CustomFieldValueId: cfValueId,
              CustomFieldId: cf.CustomFieldId,
              FieldValue: displayValue,
              Value: saveValue,
              FieldName: cf.FieldName,
              FieldTypeId: cf.FieldTypeId,
              LayerId: column.layerId ?? cf.LayerId,
              ConversationId: conversation.ConversationId,
              ServiceUrl: cf.ServiceUrl ?? '',
              TextField: cf.TextField ?? '',
              ValueField: cf.ValueField ?? '',
              OrderInLayer: cf.OrderInLayer,
              CoreTaskId: conversation.CoreTaskId ?? conversation.ConversationId,
              PreviousValue: undefined,
              SelectedValuesText: undefined,
              SocketConnectionId: '',
            });
            saveTypes.push(cfValueId > 0 ? 'save' : 'savePost');
          }
          else
          {
            // Other field types (text, multiline, hyperlink, etc.)
            const cfValueId = cf.CustomFieldValueId ?? 0;
            saveRequests.push({
              CustomFieldValueId: cfValueId,
              CustomFieldId: cf.CustomFieldId,
              FieldValue: displayValue,
              Value: saveValue,
              FieldName: cf.FieldName,
              FieldTypeId: cf.FieldTypeId,
              LayerId: column.layerId ?? cf.LayerId,
              ConversationId: conversation.ConversationId,
              ServiceUrl: cf.ServiceUrl ?? '',
              TextField: cf.TextField ?? '',
              ValueField: cf.ValueField ?? '',
              OrderInLayer: cf.OrderInLayer,
              CoreTaskId: conversation.CoreTaskId ?? conversation.ConversationId,
              PreviousValue: undefined,
              SelectedValuesText: undefined,
              SocketConnectionId: '',
            });
            saveTypes.push(cfValueId > 0 ? 'save' : 'savePost');
          }
          fieldUpdates.push({
            conversationId: conversation.ConversationId,
            coreTaskId:
              conversation.CoreTaskId ?? (conversation as any).CoreTaskPhaseId ?? undefined,
            layerId: column.layerId!,
            customFieldId: cf.CustomFieldId,
            value: saveValue,
            display: displayValue,
          });
        }
        displayUpdates.push({ visualRow, prop: key, display: displayValue });
      }

      if (saveRequests.length === 0)
      {
        setFillSaving(false);
        return;
      }

      // Optimistic display update: show resolved names (important for combo/multi cells)
      displayUpdates.forEach(({ visualRow, prop, display }) =>
      {
        hot.setDataAtRowProp(visualRow, prop, display, 'fill-save');
      });

      const ok = await gridViewService.saveMultipleFieldValues(saveRequests, saveTypes);

      if (ok)
      {
        fieldUpdates.forEach(({ conversationId, layerId, customFieldId, value, display }) =>
        {
          updateField(conversationId, layerId, customFieldId, value, display);
        });
        // Notify InfoWindow and other subscribers once per affected conversation
        const notifiedIds = new Set<number>();
        fieldUpdates.forEach(({ conversationId, coreTaskId, layerId, value }) =>
        {
          if (!notifiedIds.has(conversationId))
          {
            notifiedIds.add(conversationId);
            conversationBus.emit('field-updated', {
              conversationId,
              coreTaskId,
              fieldName: '',
              value,
              layerId,
            });
          }
        });
      }

      setFillSaving(false);
    },
    [columnMap, activeConversations, configValueId, projectId, updateField],
  );

  // afterCopy — show clipboard notification
  const handleAfterCopy = useCallback(() =>
  {
    if (copyMsgTimerRef.current) clearTimeout(copyMsgTimerRef.current);
    setShowCopyMsg(true);
    copyMsgTimerRef.current = setTimeout(() => setShowCopyMsg(false), 2000);
  }, []);

  // afterChange — route fill/paste to batch handler; skip own programmatic changes
  const handleAfterChange = useCallback(
    (changes: [number, string | number, any, any][] | null, source: string) =>
    {
      if (!changes || source === 'loadData')
      {
        return;
      }
      if (source === 'save-field' || source === 'title-rename' || source === 'fill-save')
      {
        return;
      }

      if (source === 'Autofill.fill' || source === 'CopyPaste.paste')
      {
        handleFillRange(changes);
        return;
      }
    },
    [handleFillRange],
  );

  // Prevent HOT from selecting entire columns when the user clicks a column header.
  // beforeOnCellMouseDown returning false is not always honored by HOT 14 for header clicks,
  // so we also deselect immediately in afterOnCellMouseDown as a reliable fallback.
  const handleBeforeOnCellMouseDown = useCallback((_e: MouseEvent, coords: any): boolean | void =>
  {
    if (coords?.row === -1) return false;
  }, []);

  const handleAfterOnCellMouseDown = useCallback((_e: MouseEvent, coords: any) =>
  {
    if (coords?.row === -1)
    {
      hotRef.current?.hotInstance?.deselectCell();
    }
  }, []);

  // ==================== Context Menu ====================

  const handleAfterOnCellContextMenu = useCallback(
    (event: MouseEvent, coords: { row: number; col: number }) =>
    {
      event.preventDefault();
      const col = visibleColumns[coords.col] ?? null;
      setContextMenu({ x: event.clientX, y: event.clientY, column: col });
    },
    [visibleColumns],
  );

  const handleHideColumn = useCallback(async () =>
  {
    if (!contextMenu?.column) return;
    setContextMenu(null);
    await hideColumn(getColumnApiKey(contextMenu.column));
    refresh();
  }, [contextMenu, hideColumn, refresh]);

  const handleShowColumn = useCallback(
    async (key: string) =>
    {
      setContextMenu(null);
      await showColumn(key);
      refresh();
    },
    [showColumn, refresh],
  );

  const handleApplyManageColumns = useCallback(
    async (newHiddenKeys: string[]) =>
    {
      await applyHiddenColumns(newHiddenKeys);
      refresh();
    },
    [applyHiddenColumns, refresh],
  );

  // ==================== Export ====================

  /** Strip HTML tags and decode basic entities for plain-text export */
  const stripHtmlForExport = (value: string): string =>
  {
    return value
      .replace(/<p>/gi, '')
      .replace(/<\/p>/gi, ' ')
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<\/a>/gi, ' ')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .trim();
  };

  const handleExport = useCallback(async () =>
  {
    const countY = visibleColumns.length;
    const countX = activeRows.length;
    const totalSheetCols = countY + 2; // +1 Card Type, +1 Link
    const webUrl = import.meta.env.VITE_WEB_URL || '';

    // ── Helper: CSS "#RRGGBB" → ExcelJS ARGB "FFRRGGBB" ──────────────────────
    const toArgb = (color: string): string =>
    {
      const hex = color.replace('#', '').trim();
      if (/^[0-9A-Fa-f]{6}$/.test(hex)) return `FF${hex.toUpperCase()}`;
      if (/^[0-9A-Fa-f]{3}$/.test(hex))
      {
        const [r, g, b] = hex.split('');
        return `FF${r}${r}${g}${g}${b}${b}`.toUpperCase();
      }
      return 'FF000000';
    };

    // ── Map sheet column index (1-based in ExcelJS) → GridColumn ─────────────
    // col=1 → title, col=2 → Card Type (null), col=3..countY+1 → dynamic, col=countY+2 → Link (null)
    const getColForIdx = (col: number): GridColumn | null =>
    {
      if (col === 1) return visibleColumns[0] ?? null;
      if (col === 2) return null; // Card Type
      if (col === totalSheetCols) return null; // Link
      return visibleColumns[col - 2] ?? null; // col-2 = y (1-indexed offset)
    };

    // ── Build workbook ────────────────────────────────────────────────────────
    const workbook = new ExcelJS.Workbook();
    const sheetName = sanitizeSheetName(dashboardName, 'Sheet1');
    const sheet = workbook.addWorksheet(sheetName);

    // ── Column widths (set before adding rows) ────────────────────────────────
    for (let col = 1; col <= totalSheetCols; col++)
    {
      let width = 20;
      if (col === 1) width = 40;
      else if (col === 2)
        width = 14; // Card Type
      else if (col === 3)
        width = 14; // Created At
      else if (col === totalSheetCols) width = 50; // Link
      sheet.getColumn(col).width = width;
    }

    // ── Row 1: Layer group ────────────────────────────────────────────────────
    const layerRow = sheet.addRow(new Array(totalSheetCols).fill(''));
    layerRow.height = 18;

    // Accumulate layer groups and apply merged+colored cells
    let grpStart = -1;
    let grpLayerId: number | undefined;
    let grpLayerName = '';
    let grpLayerColor = '';

    const flushGroup = (endCol: number) =>
    {
      if (grpStart < 0) return;
      const cell = layerRow.getCell(grpStart);
      cell.value = grpLayerName;
      cell.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: toArgb(grpLayerColor) } };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      if (endCol > grpStart)
      {
        sheet.mergeCells(1, grpStart, 1, endCol);
      }
      grpStart = -1;
    };

    for (let col = 1; col <= totalSheetCols; col++)
    {
      const gc = getColForIdx(col);
      const lid = gc?.layerId;
      if (lid != null)
      {
        if (grpStart >= 0 && grpLayerId === lid)
        {
          // extend group
        }
        else
        {
          flushGroup(col - 1);
          grpStart = col;
          grpLayerId = lid;
          grpLayerName = gc!.layerName ?? '';
          grpLayerColor = gc!.layerColor ?? '';
        }
      }
      else
      {
        flushGroup(col - 1);
      }
    }
    flushGroup(totalSheetCols);

    // ── Row 2: Detail headers ─────────────────────────────────────────────────
    const headerValues: string[] = [];
    for (let y = 0; y < countY; y++)
    {
      headerValues.push(visibleColumns[y]?.title ?? '');
      if (y === 0) headerValues.push('Card Type');
    }
    headerValues.push('Link');

    const headerRow = sheet.addRow(headerValues);
    headerRow.height = 18;
    headerRow.eachCell((cell, col) =>
    {
      const gc = getColForIdx(col);
      cell.font = {
        bold: true,
        size: 10,
        color: { argb: gc?.layerColor ? toArgb(gc.layerColor) : 'FF000000' },
      };
      cell.border = { bottom: { style: 'thin', color: { argb: 'FFD0D0D0' } } };
      cell.alignment = { vertical: 'middle', horizontal: 'left' };
    });

    // ── AutoFilter on the header row (row 2) ──────────────────────────────────
    sheet.autoFilter = {
      from: { row: 2, column: 1 },
      to: { row: 2, column: totalSheetCols },
    };

    // ── Data rows ─────────────────────────────────────────────────────────────
    for (let x = 0; x < countX; x++)
    {
      const conv = activeConversations[x];
      const cardType = (conv as any)?.SideMenuText ?? '';
      const rowValues: (string | number | null)[] = [];

      for (let y = 0; y < countY; y++)
      {
        const col = visibleColumns[y]!;
        let cellValue = String(activeRows[x]?.[col.key] ?? '');
        if (cellValue && isNaN(Number(cellValue)))
        {
          cellValue = stripHtmlForExport(cellValue);
        }
        rowValues.push(cellValue || null);
        if (y === 0) rowValues.push(cardType || null);
      }

      const convId = conv?.ConversationId ?? '';
      rowValues.push(convId ? `${webUrl}Dashboard/ConversationDetails/#/${convId}` : null);
      sheet.addRow(rowValues);
    }

    // ── Download ──────────────────────────────────────────────────────────────
    const d = new Date();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const fileName = `Grid_${mm}-${dd}-${d.getFullYear()}`;

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileName}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [visibleColumns, activeRows, activeConversations, dashboardName]);

  useImperativeHandle(ref, () => ({ exportToExcel: handleExport }), [handleExport]);

  // ==================== Render ====================

  // containerRef is always mounted so ResizeObserver can measure height regardless of state.
  // HotTable requires an explicit pixel height — percentage heights don't resolve in flex containers.
  const hotHeight = containerHeight > 0 ? containerHeight : '100%';

  // afterRender: HOT writes style="height:Npx" on <tr> elements after its own sync.
  // Those inline styles override CSS. We force all header <tr>s to the same height
  // after HOT finishes so all clones (top, inline_start, corner) stay aligned.
  const handleAfterRender = useCallback(() =>
  {
    const container = containerRef.current;
    if (!container) return;
    // Target th, not tr: in table layout tr.style.height is just a hint (min-height).
    // Setting height + overflow on each th forces the cell to exactly GRID_HEADER_HEIGHT
    // across all HOT clones (top, inline_start, corner).
    container.querySelectorAll<HTMLElement>('.handsontable thead tr th').forEach(th =>
    {
      th.style.height = `${GRID_HEADER_HEIGHT}px`;
      th.style.maxHeight = `${GRID_HEADER_HEIGHT}px`;
      th.style.overflow = 'hidden';
    });
  }, []);

  // afterRenderer — add a native title tooltip to every data cell so truncated
  // values are readable on hover without needing to resize the column.
  const handleAfterRenderer = useCallback(
    (TD: HTMLTableCellElement, _row: number, _col: number, _prop: string | number, value: any) =>
    {
      const text = value != null ? String(value).trim() : '';
      if (text)
      {
        TD.title = text;
      }
      else
      {
        TD.removeAttribute('title');
      }
    },
    [],
  );

  // Dynamic columns — only the visible subset (fixed cols always shown)
  const dynamicColumns = visibleColumns.slice(2);

  return (
    <div
      ref={containerRef}
      className="gridview-container"
      style={{
        paddingBottom: hasScrollbar && shouldShowNoMoreItems ? '40px' : undefined,
      }}
    >
      {loading ? (
        <GridViewSkeleton />
      ) : rows.length === 0 ? (
        <GridViewEmpty />
      ) : (
        <GridViewContext.Provider value={contextValue}>
          <HotTable
            ref={hotRef}
            data={activeRows}
            rowHeaders={true}
            rowHeights={30}
            columnHeaderHeight={GRID_HEADER_HEIGHT}
            licenseKey={GRID_LICENSE_KEY}
            themeName="ht-theme-main"
            fixedColumnsStart={1}
            manualColumnResize={!!projectId}
            manualRowResize={false}
            autoRowSize={false}
            autoColumnSize={false}
            viewportRowRenderingOffset={30}
            viewportColumnRenderingOffset={2}
            fillHandle={{ direction: 'vertical', autoInsertRow: false }}
            selectionMode="multiple"
            outsideClickDeselects={false}
            height={hotHeight}
            stretchH="none"
            colHeaders={colHeaders}
            cells={cells}
            afterGetColHeader={handleAfterGetColHeader as any}
            beforeChange={handleBeforeChange as any}
            beforeBeginEditing={handleBeforeBeginEditing as any}
            beforeOnCellMouseDown={handleBeforeOnCellMouseDown as any}
            afterOnCellMouseDown={handleAfterOnCellMouseDown as any}
            afterScrollVertically={handleAfterScrollVertically}
            afterChange={handleAfterChange as any}
            afterCopy={handleAfterCopy as any}
            afterRender={handleAfterRender}
            afterRenderer={handleAfterRenderer as any}
            afterOnCellContextMenu={handleAfterOnCellContextMenu as any}
          >
            {/* Fixed col 0: title with React renderer */}
            <HotColumn data="col-title" width={GRID_TITLE_COLUMN_WIDTH}>
              <GridTitleCell hot-renderer />
            </HotColumn>

            {/* Fixed col 1: creation date — read-only */}
            <HotColumn data="col-created-at" width={GRID_DEFAULT_COLUMN_WIDTH} />

            {/* Dynamic columns — one per unique (LayerId, CustomFieldId) */}
            {dynamicColumns.map(col => (
              <HotColumn
                key={col.key}
                data={col.key}
                width={col.width ?? GRID_DEFAULT_COLUMN_WIDTH}
              />
            ))}
          </HotTable>

          {fillSaving && <div className="gridview-loading-overlay">Saving fields…</div>}

          {loadingMore && <div className="gridview-loading-more">Loading more…</div>}

          {shouldShowNoMoreItems && !loadingMore && (
            <div
              className={`gridview-no-more-items ${hasScrollbar ? 'gridview-no-more-items-scrollable' : 'gridview-no-more-items-fixed'}`}
              style={noMoreItemsPosition !== null ? { top: `${noMoreItemsPosition}px` } : undefined}
            >
              No More Items
            </div>
          )}

          {/* Floating cell editor — rendered outside HotTable (uses portal internally) */}
          {editingCell && (
            <GridCellEditor
              editingCell={editingCell}
              onSave={handleCellSave}
              onCancel={() => setEditingCell(null)}
              configValueId={configValueId}
              projectId={projectId}
              getCellRect={getEditingCellRect}
              onCommitWithEnter={handleCommitWithEnter}
              onOutsideClick={handleEditorOutsideClick}
            />
          )}

          {/* Right-click context menu — rendered in <body> via createPortal */}
          {contextMenu && (
            <GridContextMenu
              x={contextMenu.x}
              y={contextMenu.y}
              targetColumn={contextMenu.column}
              allDynamicColumns={columns.slice(2)}
              hiddenKeys={hiddenKeys}
              onHide={handleHideColumn}
              onShow={handleShowColumn}
              onManageColumns={() =>
              {
                setContextMenu(null);
                setManageColumnsOpen(true);
              }}
              onClose={() => setContextMenu(null)}
            />
          )}

          {/* Manage Columns modal */}
          <ManageColumnsModal
            open={manageColumnsOpen}
            onClose={() => setManageColumnsOpen(false)}
            allDynamicColumns={columns.slice(2)}
            hiddenKeys={hiddenKeys}
            onApply={handleApplyManageColumns}
          />

          <DuplicateCard
            ref={duplicateCardRef}
            config={{
              apiUrl: actionMenuConfig.api.url,
              authToken: actionMenuConfig.api.token,
              SocketConnectionId: socketService.connectionId,
              attachReactFunctions: (fns: any) =>
              {
                duplicateCardInitRef.current = fns.initConfig;
              },
              DashboardId: 0,
              functions: {
                loadingBarStart: () => {},
                loadingBarComplete: () => {},
                addConversation: () =>
                {
                  refresh();
                },
              },
              params: { Conversation: {} },
            }}
            UpdateCustomFields={() => {}}
          />
        </GridViewContext.Provider>
      )}

      {/* Copy-to-clipboard notification */}
      {showCopyMsg &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              top: '64px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: '#62accb',
              color: '#fff',
              padding: '8px 20px',
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: 500,
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              pointerEvents: 'none',
              zIndex: 9999,
              whiteSpace: 'nowrap',
            }}
          >
            Value has been copied to clipboard.
          </div>,
          document.body,
        )}
    </div>
  );
});

export default GridView;
